import { useParams, Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import blogSeoContentWriting from "@/assets/blog-seo-content-writing.jpg";
import blogEcommerceSeo from "@/assets/blog-ecommerce-seo.jpg";
import blogYoutubeSeo from "@/assets/blog-youtube-seo.jpg";

const blogsData: Record<string, {
  title: string;
  date: string;
  image: string;
  content: string[];
}> = {
  "seo-content-writing": {
    title: "What Is SEO Content Writing? Beginner's Complete Guide",
    date: "17 March 2026",
    image: blogSeoContentWriting,
    content: [
      "Do you want your website or a business to rank higher on search engines? But even after publishing content, your pages don't seem to get the traffic they deserve? The problem isn't just about writing — it's about writing the right way for both search engines and readers.",
      "SEO content writing is the art and science of creating content that ranks well on search engines while engaging human readers. It involves strategically placing keywords, optimizing meta tags, structuring content with headers, and ensuring readability — all while delivering genuine value to your audience.",
      "The key components of SEO content writing include keyword research, understanding search intent, creating compelling headlines, writing meta descriptions, using internal and external links, and optimizing images with alt text. Each of these elements works together to improve your content's visibility.",
      "For beginners, the most important thing is to start with thorough keyword research. Tools like Google Keyword Planner, Ahrefs, and SEMrush can help you find keywords with good search volume and manageable competition. Once you have your target keywords, naturally incorporate them into your content.",
      "Remember, quality always trumps quantity. Search engines are increasingly sophisticated and can detect thin, keyword-stuffed content. Focus on creating comprehensive, well-researched articles that genuinely help your readers solve problems or answer questions.",
    ],
  },
  "ecommerce-seo": {
    title: "What Is E-commerce SEO? Complete Guide for Online Stores",
    date: "5 March 2026",
    image: blogEcommerceSeo,
    content: [
      "Running an online store is no longer just about having great products; it's about being found at the exact moment potential customers are searching for what you sell. E-commerce SEO is the practice of optimizing your online store to rank higher in search engine results pages.",
      "Unlike traditional SEO, e-commerce SEO requires a unique approach. You're dealing with product pages, category pages, and a structure that can become complex as your inventory grows. The goal is to make each product page rank for relevant search terms while maintaining a clean site architecture.",
      "Key elements of e-commerce SEO include optimizing product titles and descriptions with relevant keywords, creating unique content for each product page, implementing structured data markup for rich snippets, optimizing images for speed and search, and building a logical site hierarchy.",
      "Technical SEO is particularly important for e-commerce sites. Page speed, mobile responsiveness, secure HTTPS protocols, and proper URL structures all play crucial roles in how search engines rank your store. A slow-loading product page can cost you both rankings and sales.",
      "Don't forget about user experience (UX). Search engines increasingly factor in user behavior signals like bounce rate, time on page, and click-through rates. A well-designed, easy-to-navigate store not only converts better but also ranks higher.",
    ],
  },
  "youtube-seo": {
    title: "What Is YouTube SEO and How Does it Work in 2026?",
    date: "27 February 2026",
    image: blogYoutubeSeo,
    content: [
      "YouTube is the second most visited site in the whole world after Google. YouTube has become its own search engine, with over 2 billion logged-in users visiting every month. Understanding YouTube SEO is essential for anyone looking to grow their video presence.",
      "YouTube SEO involves optimizing your videos, playlists, and channel to rank higher in YouTube's organic search results. Unlike Google SEO, YouTube's algorithm considers factors like watch time, engagement (likes, comments, shares), click-through rate, and session duration.",
      "The foundation of YouTube SEO starts with keyword research specific to the platform. Use YouTube's search suggest feature, tools like TubeBuddy or vidIQ, and Google Trends to find what your target audience is searching for on YouTube.",
      "Video optimization includes crafting compelling titles with your target keyword, writing detailed descriptions (at least 200 words), adding relevant tags, creating eye-catching custom thumbnails, and using cards and end screens to boost engagement and watch time.",
      "In 2026, YouTube's algorithm has evolved to prioritize viewer satisfaction over pure view counts. This means creating content that keeps viewers watching, encourages them to watch more videos, and builds a loyal subscriber base is more important than ever.",
    ],
  },
};

const BlogPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const blog = blogsData[slug || ""];

  if (!blog) {
    return (
      <PageLayout title="Blog Post Not Found" subtitle="The requested article doesn't exist.">
        <div className="py-20 text-center">
          <Link to="/#blog" className="inline-flex px-8 py-3 rounded-full gradient-cta text-primary-foreground font-display font-semibold text-sm">
            Back to Blog
          </Link>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={blog.title} subtitle={blog.date}>
      <article className="py-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="mb-10 rounded-2xl overflow-hidden">
            <img
              src={blog.image}
              alt={blog.title}
              className="w-full h-auto object-cover"
            />
          </div>

          {blog.content.map((p, i) => (
            <p key={i} className="text-muted-foreground leading-relaxed mb-6 text-base">{p}</p>
          ))}

          <div className="mt-12 pt-8 border-t border-border text-center">
            <p className="text-foreground font-display font-bold mb-4">Ready to grow your business?</p>
            <a
              href="https://wa.me/+923058559591"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex px-8 py-4 rounded-full gradient-cta text-primary-foreground font-display font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              Book A Free Consultation
            </a>
          </div>
        </div>
      </article>
    </PageLayout>
  );
};

export default BlogPage;
